/** Tab / chrome icons — Heroicons solid (crisp) + two SF-matched customs. */

import type { ComponentType, SVGProps } from "react";
import {
  AcademicCapIcon,
  ArrowUpTrayIcon,
  CakeIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  ChevronLeftIcon,
  Cog6ToothIcon,
  FireIcon,
  GiftIcon,
  HandRaisedIcon,
  HeartIcon,
  HomeIcon,
  MapIcon,
  NewspaperIcon,
  PlusIcon,
  SparklesIcon,
  Squares2X2Icon,
  TrophyIcon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";

type IconProps = { className?: string };

type HeroIcon = ComponentType<SVGProps<SVGSVGElement>>;

function wrap(Icon: HeroIcon) {
  return function WrappedIcon({ className }: IconProps) {
    return <Icon className={className} aria-hidden focusable="false" />;
  };
}

/** tree.fill — round canopy sitting on a clear trunk */
export function IconTree({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="currentColor"
      aria-hidden
      focusable="false"
    >
      <ellipse cx="12" cy="8.75" rx="7.25" ry="6.5" />
      <path d="M10.25 14.75h3.5c.55 0 1 .45 1 1V21c0 .55-.45 1-1 1h-3.5c-.55 0-1-.45-1-1v-5.25c0-.55.45-1 1-1z" />
    </svg>
  );
}

export const IconHome = wrap(HomeIcon);
export const IconPeople = wrap(UserGroupIcon);

/** gamecontroller.fill — body + evenodd cutouts */
export function IconGame({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="currentColor"
      fillRule="evenodd"
      clipRule="evenodd"
      aria-hidden
      focusable="false"
    >
      <path d="M8 6.5h8A5.5 5.5 0 0 1 21.5 12v1A5.5 5.5 0 0 1 16 18.5H8A5.5 5.5 0 0 1 2.5 13v-1A5.5 5.5 0 0 1 8 6.5ZM7.75 10a.75.75 0 0 0-.75.75V12H5.75a.75.75 0 0 0 0 1.5H7v1.25a.75.75 0 0 0 1.5 0V13.5h1.25a.75.75 0 0 0 0-1.5H8.5v-1.25A.75.75 0 0 0 7.75 10Zm7.5.25a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5Zm2.75 2.5a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5Z" />
    </svg>
  );
}

export const IconNews = wrap(NewspaperIcon);
export const IconGear = wrap(Cog6ToothIcon);
export const IconShare = wrap(ArrowUpTrayIcon);
export const IconMap = wrap(MapIcon);
export const IconGrid = wrap(Squares2X2Icon);
export const IconSparkles = wrap(SparklesIcon);
export const IconCake = wrap(CakeIcon);
export const IconHeart = wrap(HeartIcon);
export const IconWave = wrap(HandRaisedIcon);
export const IconFlame = wrap(FireIcon);
export const IconParty = wrap(GiftIcon);
export const IconTrophy = wrap(TrophyIcon);
export const IconFeedback = wrap(ChatBubbleLeftRightIcon);
export const IconPlus = wrap(PlusIcon);
export const IconChevronLeft = wrap(ChevronLeftIcon);
export const IconBrain = wrap(AcademicCapIcon);
export const IconCheck = wrap(CheckIcon);
export const IconX = wrap(XMarkIcon);
